import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormControlLabel,
	MenuItem,
	Paper,
	Radio,
	RadioGroup,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { api } from "api";
import { UnauthenticatedLayout } from "components/UnauthenticatedLayout";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { routes } from "routes";
import { useKeycloak } from "@react-keycloak/web";
import { useAuthStatus } from "helpers/useAuthStatus";

interface LocationState {
	email?: string;
	firstName?: string;
	lastName?: string;
	keycloakId?: string;
}

interface School {
	schoolId: string;
	schoolName: string;
}

export const GoogleCompleteProfilePage = (): JSX.Element => {
	const history = useHistory();
	const location = useLocation<LocationState>();
	const { keycloak } = useKeycloak();
	const { setRole } = useAuthStatus();

	const provisionData = location.state;

	const [schoolAction, setSchoolAction] = useState<"create" | "join">("create");
	const [schoolName, setSchoolName] = useState("");
	const [schoolId, setSchoolId] = useState("");
	const [schools, setSchools] = useState<School[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (!provisionData?.email) {
			history.replace(routes._.signIn.path);
			return;
		}
		void api.getPublicSchools().then((res) => {
			if (res.status === 200) setSchools(res.data);
		});
	}, [provisionData, history]);

	const handleSubmit = async (): Promise<void> => {
		if (schoolAction === "create" && !schoolName.trim()) {
			setError("School name is required.");
			return;
		}
		if (schoolAction === "join" && !schoolId) {
			setError("Please select a school.");
			return;
		}
		if (!keycloak.token) {
			setError("Session expired — please sign in again.");
			return;
		}

		setLoading(true);
		setError("");

		try {
			const res = await api.provisionGoogleTeacher(
				{
					school_action: schoolAction,
					school_name: schoolAction === "create" ? schoolName.trim() : undefined,
					school_id: schoolAction === "join" ? schoolId : undefined,
				},
				{ headers: { Authorization: `Bearer ${keycloak.token}` } },
			);

			if (res.status === 201) {
				const authStatus = await api.authStatus();
				if (authStatus.status === 200) setRole(authStatus.data.role);
				history.replace(routes.path);
			} else if (res.status === 409) {
				setError("This email is already registered. Try signing in instead.");
			} else {
				setError("Something went wrong. Please try again.");
			}
		} catch {
			setError("Network error. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	if (!provisionData?.email) return <></>;

	return (
		<UnauthenticatedLayout>
			<Paper sx={{ p: 5, maxWidth: 480, mx: "auto", mt: 8 }}>
				<Stack spacing={3}>
					<Typography variant="h5">Complete Your Profile</Typography>
					<Typography variant="body2" color="text.secondary">
						Welcome, {provisionData.firstName || provisionData.email}. You are signing up as a
						teacher. Please set up your school.
					</Typography>

					{error && <Alert severity="error">{error}</Alert>}

					<FormControl>
						<RadioGroup
							row
							value={schoolAction}
							onChange={(e) => setSchoolAction(e.target.value as "create" | "join")}
						>
							<FormControlLabel value="create" control={<Radio />} label="Create new school" />
							<FormControlLabel value="join" control={<Radio />} label="Join existing school" />
						</RadioGroup>
					</FormControl>

					{schoolAction === "create" && (
						<TextField
							label="School Name"
							value={schoolName}
							onChange={(e) => setSchoolName(e.target.value)}
							fullWidth
						/>
					)}

					{schoolAction === "join" && (
						<Select
							value={schoolId}
							onChange={(e) => setSchoolId(e.target.value)}
							displayEmpty
							fullWidth
						>
							<MenuItem value="" disabled>
								Select a school
							</MenuItem>
							{schools.map((s) => (
								<MenuItem key={s.schoolId} value={s.schoolId}>
									{s.schoolName}
								</MenuItem>
							))}
						</Select>
					)}

					<Box>
						<Button
							variant="contained"
							onClick={handleSubmit}
							disabled={loading}
							startIcon={loading ? <CircularProgress size={16} /> : undefined}
						>
							{loading ? "Setting up…" : "Continue"}
						</Button>
					</Box>
				</Stack>
			</Paper>
		</UnauthenticatedLayout>
	);
};
